import {
  array,
  boolean,
  mixed,
  number,
  object,
  string,
  InferType,
  ValidationError,
} from "yup";
import { getError } from "../../../utils/errorMessage";
import { int, OneOf, uint } from "../base";
import { bubbleModes, cameraPresets, cameraEases, characterMotionTypes, directions, editedBy, filterPresets, labelColors } from "../consts";
import { AnimationKey, CharSlotId, ChoiceId, RevealType, StepId, TimelineId } from "../../../types";

// String schema
const charSlotId = mixed<CharSlotId>();
const animationKey = mixed<AnimationKey>();
const timelineId = mixed<TimelineId>();
const stepId = mixed<StepId>();

const characterColors = OneOf(labelColors);

const validCDNUrl = string().test('isValidCDNUrl', (v?: string) => Boolean(v && v.startsWith('https://cms.characters')));
const validAssemblerNUrl = string().test('validAssemblerNUrl', (v?: string) => Boolean(v && v.startsWith('https://sverse-character-storage') && v.includes('assembler')));

const validAnimationUrl = string().test('validAnimationUrl', (v?: string) => validCDNUrl.isValidSync(v) || validAssemblerNUrl.isValidSync(v));

const characterAnimationsTest = {
  name: 'characterAnimationsTest',
  message: "characterAnimationsTest has failed validation",
  test: (value?: Record<string, string>) => {
    if (!value) {
      return false;
    }
    const testKeys = Object.keys(value).every(k => string().isValidSync(k) && k.startsWith('a_') && k.endsWith('.json'));

    const testValues = Object.values(value).every(v => string().isValidSync(v) && validAnimationUrl.isValidSync(v));

    const isValid = testValues && testKeys;

    if (!isValid) {
      console.error('Error: characterAnimationsTest', {
        testKeys,
        testValues
      })
    }

    return isValid;
  }
};

export type ICharAnimations = Record<AnimationKey, string>;

const characterAnimations = mixed<ICharAnimations>().test(characterAnimationsTest);

const charMetadataSchema = object({
  id: string().required(),
  name: string().required(),
  color: characterColors.required(),
  skeleton: string().required(),
  skin: string().required(),
  animations: characterAnimations.required(),
  tokenId: string().required(),
  contractAddress: string().required(),
  attributes: string().default(undefined),
  version: string().default(undefined),
}).noUnknown();

export type ICharMetadata = InferType<typeof charMetadataSchema>;

const charLayoutSchema = object({
  x: number().required(),
  y: number().required(),
  scale: number().required(),
  zIndex: int.required(),
  dir: number().oneOf(directions).required(),
  layer: int.required(),
}).noUnknown();

const charSlotSchema = object({
  id: charSlotId.required(),
  layout: charLayoutSchema.required(),
  disabledOnStart: boolean().default(undefined),
  displayName: string().default(undefined),
  charMetadata: charMetadataSchema.required(),
  overrideable: boolean().default(undefined),
}).noUnknown();

export type ICharSlot = InferType<typeof charSlotSchema>;

export type ICharSlots = Record<CharSlotId, ICharSlot>;


const charSlots = mixed<ICharSlots>();

const parallaxLayerSchema = object({
  image: string().required(),
  width: number().default(undefined),
  height: number().default(undefined),
  x: number().default(undefined),
  y: number().default(undefined),
}).noUnknown();

export type IBgLayer = InferType<typeof parallaxLayerSchema>;

const parallaxSchema = object({
  width: number().required(),
  height: number().required(),
  y: number().required(),
  layers: array().of(parallaxLayerSchema).required(),
}).noUnknown();

export type IBackground = InferType<typeof parallaxSchema>;

const direction = OneOf(directions);

const bubbleLayoutSchema = object({
  x: number().required(),
  y: number().required(),
  dirX: direction.required(),
  dirY: direction.required(),
  tailOffset: number().default(undefined),
}).noUnknown();

export type IBubbleLayout = InferType<typeof bubbleLayoutSchema>;

const bubbleSchema = object({
  mode: OneOf(bubbleModes).default(undefined),
  tagname: string().default(undefined),
  tagcolor: characterColors.default(undefined),
  charSlotId: charSlotId.default(undefined),
  layout: bubbleLayoutSchema.required(),
  caption: string().required()
}).noUnknown();

export type IBubble = InferType<typeof bubbleSchema>;

const choiceOptionSchema = object({
  caption: string().required(),
  toTimeline: timelineId.required(),
}).noUnknown();

export type IChoice = InferType<typeof choiceOptionSchema>;

export type IChoices = Record<ChoiceId, IChoice>;

const choicesSchema = mixed<IChoices>();

const cameraPreset = OneOf(cameraPresets);

const cameraMotionSchema = object({
  ease: OneOf(cameraEases).default(undefined),
  duration: number().default(undefined),
  zoom: number().default(undefined),
  easeParams: mixed<Record<string, any>>().default(undefined),

  // not be used in current specs but could easily be added:
  // x: number().optional(),
  // y: number().optional(),

  // not sure we want to enable this one, it may break a few things:
  // rotation: number().optional(),
});

export type ICameraMotion = InferType<typeof cameraMotionSchema>;

const cameraSchema = object({
  charSlotId: charSlotId.default(undefined),
  preset: cameraPreset.default(undefined),
  motions: object({
    // camera motion to the specified preset
    toPreset: cameraMotionSchema.default(undefined),
    // camera motion that starts with the character speech
    alongSpeech: cameraMotionSchema.default(undefined),

    // example of other possible motions:

    // camera motion that loops throughout the character speech
    // throughoutSpeech: cameraMotionSchema.optional(),
    // camera motion that starts when the character speech has ended
    // postSpeech: cameraMotionSchema.optional(),
  }).default(undefined),
}).noUnknown();

export type ICamera = InferType<typeof cameraSchema>;

const charPosSchema = object({
  x: number().default(undefined),
  y: number().default(undefined),
}).noUnknown();

export type ICharPos = InferType<typeof charPosSchema>;

const charMotionPosSchema = object({
  x: number().required(),
  y: number().required(),
}).noUnknown();

export type ICharMotionPos = InferType<typeof charMotionPosSchema>;

const charMotionSchema = object({
  to: charMotionPosSchema.default(undefined),
  speed: number().required(),
  type: OneOf(characterMotionTypes).default(undefined),
}).noUnknown();

export type ICharMotion = InferType<typeof charMotionSchema>;

const gestureSchema = object({
  charSlotId: charSlotId.required(),
  dirX: direction.default(undefined),
  animation: animationKey.default(undefined),
  loop: boolean().default(undefined),
  charPos: charPosSchema.default(undefined),
  charMotion: charMotionSchema.default(undefined),
  visible: boolean().default(undefined),
}).noUnknown();

export type IGesture = InferType<typeof gestureSchema>;

const gestures = mixed<IGestures>();

export type IGestures = Record<CharSlotId, IGesture>;

const stepSchema = object({
  prevStep: stepId.default(undefined),
  nextStep: stepId.default(undefined),
  gestures: gestures.required(),
  parallax: parallaxSchema.default(undefined),
  camera: cameraSchema.default(undefined),
  bubble: bubbleSchema.default(undefined),
  // If it's the last step of a timeline (if not the last step of the whole story) it must have either choices or toTimeline
  choices: choicesSchema.default(undefined),
}).noUnknown();

export type IStep = InferType<typeof stepSchema>;

const steps = mixed<ISteps>();

export type ISteps = Record<StepId, IStep>;

const timelineSchema = object({
  root: timelineId.required(),
  lastStep: stepId.required(),
  firstStep: stepId.required(),
  // toTimeline can be set when timeline doesnt have choices
  toTimeline: timelineId.default(undefined),
  // mergeTimeline is set when the timeline has choices
  mergeTimeline: timelineId.default(undefined),
  steps: steps.required(),
}).noUnknown();

export type ITimeline = InferType<typeof timelineSchema>;

export type ITimelines = Record<TimelineId, ITimeline>;

export const timelinesSchema = mixed<ITimelines>();

const revealType = mixed<RevealType>();

const revealScheduleSchema = object({
  timestamp: number().required(),
  revealedTimelineId: timelineId.required(),
  type: revealType.required(),
  revealed: boolean().required(),
});

export type IRevealSchedule = InferType<typeof revealScheduleSchema>;

const storyMetadataSchema = object({
  timestamp: uint.required(),
  version: uint.required(),
  saveCount: int.required(),
  editedBy: OneOf(editedBy).required(),
  templateIndex: string().default(undefined),
  revealSchedule: revealScheduleSchema.default(undefined),
}).noUnknown();

export type IStoryMetadata = InferType<typeof storyMetadataSchema>;

export type ChoicesMade = Record<TimelineId, ChoiceId>;

const choicesMade = mixed<ChoicesMade>();

const bookmarkSchema = object({
  timelineId: timelineId.required(),
  stepId: stepId.required(),
  label: string().required(),
});

export type IBookmark = InferType<typeof bookmarkSchema>;

export type IBookmarks = IBookmark[];

export const storySchema = object({
  filterPreset: OneOf(filterPresets).required(),
  charSlots: charSlots.required(),
  metadata: storyMetadataSchema.required(),
  rootTimelineId: timelineId.required(),
  timelines: timelinesSchema.required(),
  choicesMade: choicesMade.required(),
  revealedTimelineId: timelineId.default(undefined),
  bookmarks: array().of(bookmarkSchema),
}).noUnknown();

export type IStory = InferType<typeof storySchema>;

